<template>
  <div class="video-carousel">
    <div class="video-container-wrapper rounded-[12px] overflow-hidden">
      <VideoPlayer
        ref="videoRef"
        :key="activeIndex"
        :class="`w-full h-full video_cover`"
        :url="src"
        :poster="poster"
        :autoplay="true"
        :loop="false"
        :muted="isMuted"
        :enable-fullscreen="false"
        loading="lazy"
        @ended="next"
        @time-update="timeUpdate"
      />
    </div>
    <div class="dots">
      <Dot
        v-for="(item, index) in infos"
        :key="item.title"
        :active="activeIndex === index"
        @click="goTo(index)"
      />
    </div>
    <Arrow direction="left" :disabled="prevDisable" @click="arrowClick('prev')" />
    <Arrow direction="right" :disabled="nextDisable" @click="arrowClick('next')" />
  </div>
</template>

<script setup lang="ts">
import VideoPlayer from '~/components/video-player/index.vue';
import Dot from '~/components/xDot/index.vue';
import Arrow from './arrow.vue';

export interface IInfo {
  title: string;
  url: string;
  poster: string;
  desc?: string;
  subTitle?: string;
  tabDesc?: string;
}

const props = defineProps<{
  infos: IInfo[];
}>();

const activeIndex = ref(0);
const videoRef = ref();
const src = ref(props.infos[activeIndex.value].url);
const poster = ref(props.infos[activeIndex.value].poster);

const progress = ref(0);

const isMuted = inject<boolean>('isMuted') || true;

const goTo = (index: number) => {
  activeIndex.value = index;
};

const prevDisable = computed(() => activeIndex.value === 0);
const nextDisable = computed(() => activeIndex.value === props.infos.length - 1);

watch(
  () => activeIndex.value,
  val => {
    src.value = props.infos[val].url;
    poster.value = props.infos[val].poster;
  },
);

function arrowClick(mode: 'prev' | 'next') {
  if (mode === 'prev' && !prevDisable.value) {
    prev();
  } else if (mode === 'next' && !nextDisable.value) {
    next();
  }
}

function prev() {
  if (activeIndex.value === 0) {
    activeIndex.value = props.infos.length - 1;
  } else {
    activeIndex.value--;
  }
}

function next() {
  if (activeIndex.value === props.infos.length - 1) {
    activeIndex.value = 0;
  } else {
    activeIndex.value++;
  }
}

function timeUpdate(currentTime: number) {
  progress.value = currentTime / videoRef.value.duration();
}
</script>

<style lang="scss" scoped>
.video-carousel {
  @apply relative w-full flex-col items-center;
  .video-container-wrapper {
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    margin-bottom: 16px;
    border-radius: 12px;
  }
  .video_cover {
    background-color: var(--x-back-3);
  }
  .dots {
    @apply flex-center gap-4;
  }
}
</style>
